import { Injectable } from "@angular/core";
import { HttpService } from "../../http/http";
import { UserRepository } from "../../../domain/user-repository";
import { RawUser, User, UserChannelPermission } from "../../../domain/user";
import { toMetadata, UserMapper } from "./user.mapper";
import { CountryService } from "../../../domain/country-service";
import { TenantStorageOption } from "../../../domain/tenant-storage-option";
import { Tenant } from "../../../domain/tenant";
import moment from "moment";
import { Auth, Token } from "../../auth";

@Injectable()
export class UsersRepositoryImpl implements UserRepository {
  constructor(
    private httpService: HttpService,
    private userMapper: UserMapper,
    private countries: CountryService,
    private auth: Auth,
  ) {}

  user: User;

  async init(): Promise<void> {
    // The initial response establishes the authoritative server clock and must
    // complete before the rest of the application is hydrated.
    const initResponse = await this.httpService.get("init");
    const serverUtcTimestamp = Number(initResponse.serverUtcTimestamp);
    if (!Number.isFinite(serverUtcTimestamp) || serverUtcTimestamp <= 0) {
      throw new Error(
        "The bootstrap response did not include a valid server timestamp.",
      );
    }
    const serverTime = serverUtcTimestamp * 1000;
    const initEpoch = Date.now();
    (moment as any).now = () => {
      return +new Date(serverTime + (Date.now() - initEpoch));
    };
    // Keep the authoritative clock even when a later lookup fails; retry and
    // error UI must use the same server-relative time as a successful session.

    // These lookup endpoints do not depend on one another. Fetching them in
    // parallel removes seven network round trips from the critical path before
    // the channel repository can start loading a large tenant.
    const [
      fontsResponse,
      dynamicScriptsResponse,
      timezones,
      languages,
      metaData,
      tenantStorageOptions,
      availabilityZonesResponse,
    ] = await Promise.all([
      this.httpService.get("Font"),
      this.httpService.get("DynamicScripts"),
      this.httpService.get("init/timezones"),
      this.httpService.get("init/languages"),
      this.httpService.get("init/metadata"),
      this.httpService.get("init/tenant-storage-options"),
      this.httpService.get("init/availabilityZones"),
    ]);
    const channelAvailabilityZones =
      availabilityZonesResponse?.AvailabilityZones;
    if (!Array.isArray(channelAvailabilityZones)) {
      throw new Error(
        "The bootstrap response did not include valid availability zones.",
      );
    }

    /* TODO: make countries linked to country service */
    this.user = this.userMapper.toUser(
      initResponse,
      fontsResponse,
      dynamicScriptsResponse,
      timezones,
      languages,
      metaData,
      tenantStorageOptions,
      channelAvailabilityZones,
    );
    /* Saving countries to service */
    this.countries.setCountries(this.user.countries);
  }

  getCurrentUser() {
    return this.user;
  }

  getTimezoneValueById(id: number): string {
    const index = this.user.timezones.findIndex((x) => x.id === id);
    return index > 0 ? this.user.timezones[index].value : undefined;
  }

  getUsers(
    userId: number = null,
    tenantId: number = null,
  ): Promise<Array<RawUser>> {
    return new Promise((resolve, reject) => {
      const filters = [];
      if (userId) {
        filters.push("id=" + userId.toString());
      }
      if (tenantId) {
        filters.push("tenantId=" + tenantId.toString());
      }
      const url = "user/Get?" + filters.join("&");
      console.log("users-repository->getUsers->url", url);
      this.httpService.get(url).then((response) => {
        console.log("user-repository", response);
        const rawUsers = response.map((responseUser) => {
          let trialEnds = null;
          let lastAuth = null;
          if (responseUser.trialEnds) {
            trialEnds = new Date(responseUser.trialEnds.split("T")[0]);
          }
          if (responseUser.lastAuth) {
            lastAuth = new Date(responseUser.lastAuth);
            console.log("getUsers->", responseUser.lastAuth, lastAuth);
          }
          const roles = {};
          const channelPermissions = {};
          if (responseUser.roles && responseUser.roles.length > 0) {
            responseUser.roles.map((roleName) => {
              if (roleName.length > 0) {
                roles[roleName] = true;
              }
            });
          }
          if (
            responseUser.channelPermissions &&
            responseUser.channelPermissions.length > 0
          ) {
            responseUser.channelPermissions
              // tslint:disable-next-line:max-line-length
              .map(
                (channelPermission) =>
                  (channelPermissions[channelPermission.ChannelId] =
                    new UserChannelPermission(
                      channelPermission.ChannelId,
                      channelPermission.View,
                      channelPermission.EditSchedule,
                      channelPermission.EditSettings,
                      channelPermission.ActivateChannel,
                    )),
              );
          }

          return new RawUser(
            responseUser.userId,
            responseUser.username,
            responseUser.email,
            responseUser.firstName,
            responseUser.lastName,
            //responseUser.tenantGuid,
            responseUser.managedTenantIds,
            responseUser.phone,
            roles,
            channelPermissions,
            trialEnds,
            responseUser.hasTwoFactor,
            lastAuth,
          );
        });
        resolve(rawUsers);
      });
    });
  }

  updateMetadataNames(
    metadata: string[],
    deletedMetadata: number[],
    updatedMetaDataNames: { id: number; name: string }[],
  ) {
    return new Promise((resolve, reject) => {
      return this.httpService
        .post("user/updateMetadata", {
          newMetadataNames: metadata,
          deletedMetadata: deletedMetadata,
          updatedMetaDataNames,
        })
        .then((res) => {
          if (res) {
            this.user.metadata = toMetadata(res);
            resolve(this.user.metadata);
          } else {
            reject();
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateUser(updatedUser: RawUser): Promise<void> {
    return this.httpService
      .post("user/update", {
        ...updatedUser,
      })
      .then(() => {
        console.log("user updated", updatedUser);
      });
  }

  create(newUser: RawUser, newPassword: string): Promise<void> {
    // console.log('newUser',newUser);
    return this.httpService
      .post("user/create", {
        ...newUser,
        newPassword,
      })
      .then((createdUser) => {
        newUser.userId = createdUser.Id;
        newUser.email = newUser.username;
      });
  }

  validateUsernameExists(username: string): Promise<boolean> {
    return this.httpService
      .post("user/ValidateUsernameExists", { Username: username })
      .then((result) => {
        if (result === "username doesn't exist") {
          return false;
        } else {
          return true;
        }
      });
  }
  async resetPassword(userId: number, newPassword: string): Promise<void> {
    const foundUsers = await this.getUsers(userId);
    await this.httpService.post("user/update", {
      ...foundUsers[0],
      newPassword,
    });
  }

  delete(userId: number): Promise<void> {
    return this.httpService.delete(`user/delete/${userId}`);
  }

  changeTenant(tenantId: number): Promise<void> {
    return this.httpService.post("init/changeTenants", {
      TenantId: tenantId,
    });
  }

  refreshAccessToken(): Promise<Token> {
    return this.httpService
      .get("init/refreshAccessToken")
      .then(
        (response) =>
          new Token(response.token, response.tokenType, this.auth.token.expiry),
      );
  }
}
